import type { FC } from 'react';
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { isoToBritishInput, britishInputToIso, formatBritishTime } from '@/lib/date';
import { Textarea } from '@/components/ui/textarea';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createEventSchema } from '@tyto/shared';
import { toast } from 'sonner';
type EventFormValues = z.infer<typeof createEventSchema>;

interface EventType {
  id: string;
  name: string;
  description: string;
  startDate: string;
}

interface EditEventFormProps {
  eventId: string;
  orgId: string;
  eventUpdated: () => void
  onCancel: () => void
}

export const EditEventForm: FC<EditEventFormProps> = ({ eventId, orgId, eventUpdated, onCancel }) => {
  // Fetch events from the organization
  const { data: event, isLoading, error } = useQuery({
    queryKey: ['organizationEvent', eventId],
    queryFn: async () => {
      const response = await fetch(`/api/org/${orgId}/event/${eventId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch event');
      }
      return response.json() as Promise<EventType>;
    },
  });

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          Failed to load records. Please try again.
        </div>
      ) : (
        <ActualForm
          event={event}
          orgId={orgId}
          eventUpdated={() => eventUpdated()}
          onCancel={() => onCancel()}
        />
      )}
    </>
  );
}
interface ActualFormProps {
  orgId: string;
  event: EventType;
  eventUpdated: () => void
  onCancel: () => void
}

const ActualForm: FC<ActualFormProps> = ({ event, orgId, eventUpdated, onCancel }) => {

  const queryClient = useQueryClient();
  // Setup form for creating/editing events
  const form = useForm<EventFormValues>({
    resolver: zodResolver(createEventSchema),
    defaultValues: {
      name: event.name,
      description: event.description,
      startDate: event.startDate
    },
  });

  // Mutation for updating an event
  const updateEventMutation = useMutation({
    mutationFn: async ({ eventId, data }: { eventId: string; data: EventFormValues }) => {
      const response = await fetch(`/api/org/${orgId}/event/${eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update event');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationEvents'] });
      toast.success("Event updated successfully");
      eventUpdated()
      form.reset();
    },
    onError: (error) => {
      console.error('Error updating event:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update event');
    }
  });

  // Handle form submission for editing an event
  const onEditSubmit = (data: EventFormValues) => {
    if (event) {
      updateEventMutation.mutate({ eventId: event.id, data });
    }
  };

  // Set form values when editing an event
  React.useEffect(() => {
    if (event) {
      form.reset({
        name: event.name,
        description: event.description,
        startDate: event.startDate
      });
    }
  }, [event, form]);


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onEditSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Event Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter event name..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Start Date and Time</FormLabel>
              <FormControl>
                <Input
                  type="datetime-local"
                  value={isoToBritishInput(field.value)}
                  onChange={x => field.onChange(britishInputToIso(x.target.value))}
                />
              </FormControl>
              <div className="text-xs text-muted-foreground mt-1">
                {field.value && formatBritishTime(field.value)}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description (Markdown)</FormLabel>
              <FormControl>
                <MarkdownEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter event description using markdown..."
                  className="min-h-[120px]"
                />
              </FormControl>
              <FormMessage />
              <p className="text-xs text-muted-foreground mt-1">Supports markdown formatting</p>
            </FormItem>
          )}
        />

        <DialogFooter className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => onCancel()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={updateEventMutation.isPending}
          >
            {updateEventMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Event'
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}